import { User } from '@/api/types/public';
import { getStoreState } from '@/store';
import logger from '@/utils/client-logger';
import { ZL_USERINFO } from '@/utils/constants';
import { env } from '@/utils/env';
import CryptoJS from 'crypto-js';
import { Provider, ProviderType, TrackingEvent } from '../types';

interface SnapchatConfig {
  appKey: string;
  isEnabled?: boolean;
}

// Define Snapchat interface
interface SnaptrFunction {
  (...args: any[]): void;
  handleRequest?: (...args: any[]) => void;
  queue: any[];
}

// Window interface已在index.d.ts中统一定义

export class SnapchatProvider implements Provider {
  type = ProviderType.SNAPCHAT;
  isEnabled: boolean;
  loggingEnabled = false; // 公开属性，支持ReportingCenter访问
  private initialized = false;
  private config: SnapchatConfig;

  constructor(config: SnapchatConfig) {
    this.config = config;
    this.isEnabled = config.isEnabled ?? true;
    // 默认在本地环境启用日志
    this.loggingEnabled = env.NEXT_PUBLIC_ENV === 'local';
  }

  async init(): Promise<boolean> {
    try {
      if (typeof window === 'undefined') return false;
      if (this.initialized) return true;

      // Initialize Snapchat Pixel using the original script logic
      this.initializeSnapchatPixel();

      // Get user info for initialization
      const userInfo = this.getUserInfo();

      // Initialize with app key and user data
      const initData = userInfo.email
        ? {
            user_email: userInfo.email.toLowerCase(),
          }
        : {};

      window.snaptr('init', this.config.appKey, initData);

      // Track initial PAGE_VIEW
      window.snaptr('track', 'PAGE_VIEW');

      this.initialized = true;
      return true;
    } catch (error) {
      logger.error('Failed to initialize Snapchat Pixel:', error);
      return false;
    }
  }

  track(event: TrackingEvent): void {
    if (!this.initialized || typeof window === 'undefined') return;

    try {
      // 获取当前的 store 状态
      const storeState = getStoreState();
      // eslint-disable-next-line prefer-destructuring
      const userInfo = storeState.userInfo;

      // 先获取事件的原始参数（来自适配器）
      const params: any = { ...event.properties };

      // 再附加用户信息
      if (userInfo.id) {
        params.uuid_c1 = `${userInfo.id}`;
      }
      if (userInfo.email) {
        const email = userInfo.email.toLowerCase();
        params.user_email = email;
        const hash = CryptoJS.SHA256(params.user_email);
        params.user_hashed_email = hash.toString(CryptoJS.enc.Hex);
      }
      if (userInfo.telephone) {
        const telephone = `${userInfo.tel_code}${userInfo.telephone}`;
        params.user_phone_number = telephone.replace(/\D/g, '');
        const hash = CryptoJS.SHA256(params.user_phone_number);
        params.user_hashed_phone_number = hash.toString(CryptoJS.enc.Hex);
      }

      // 使用统一的日志控制方式
      if (this.loggingEnabled) {
        logger.log(`Snapchat, Event: ${event.eventName}`, params);
      }

      window.snaptr(event.commandType || 'track', event.eventName, params);
    } catch (error) {
      logger.error('Snapchat tracking error:', error);
    }
  }

  /**
   * 启用Snapchat日志
   */
  enableLog(): void {
    this.loggingEnabled = true;
  }

  /**
   * 禁用Snapchat日志
   */
  disableLog(): void {
    this.loggingEnabled = false;
  }

  isInitialized(): boolean {
    return this.initialized;
  }

  private initializeSnapchatPixel(): void {
    // Replicate the original Snapchat Pixel initialization script
    const e = window;
    const t = document;
    const n = 'https://sc-static.net/scevent.min.js';

    if (e.snaptr) return;

    // eslint-disable-next-line no-multi-assign
    const a = (e.snaptr = function () {
      if (a.handleRequest) {
        // eslint-disable-next-line prefer-spread, prefer-rest-params
        a.handleRequest.apply(a, arguments);
      } else {
        // eslint-disable-next-line prefer-rest-params
        a.queue.push(arguments);
      }
    } as SnaptrFunction);

    a.queue = [];

    const s = 'script';
    const r = t.createElement(s) as HTMLScriptElement;
    r.async = true;
    r.src = n;

    const u = t.getElementsByTagName(s)[0];
    u.parentNode.insertBefore(r, u);
  }

  private getUserInfo(): User {
    let userInfo: User = {};

    try {
      const userInfoStr = localStorage.getItem(ZL_USERINFO);
      if (userInfoStr) {
        userInfo = JSON.parse(userInfoStr);
      }
    } catch (err) {
      logger.log('Failed to get user info:', err);
      userInfo = {};
    }

    return userInfo;
  }
}
